/* eslint-disable */
import React, { useContext, useState } from "react";
import Loader from "../components/common/loader/loader";
import i18n from "../../localization/i18n";
import * as rk from "../../localization/resourceKeys";
import Header from "../components/myVW/Header";
import Link from "../components/common/link/link";
import LoginForm from "../components/myVWLogin/loginForm";
import { MyVWMarketingBanner } from "../components/common/banner/MyVWMarketingBanner";
import Logger from "../../logger";
import styles from "./myVWLogin.module.scss";
import { SessionContext } from "../../context/sessionContext";
import { useNavigate } from "react-router-dom";
import { useMyVW } from "../../context/myVWContext";
import MyVWPaperlessConsent from "./myVWPaperlessConsent";
import { useMyVWQueryParams } from "../hooks/useMyVWQueryParams";
import enrollInPaperless from "../services/enrollInPaperless";

type LoginStep = 'loginForm' | 'consent'

const MyVWLogin: React.FC = () => {
  const { sessionDetails } = useContext(SessionContext)!;
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const stepParam = searchParams.get('step');
  const [step, setStep] = useState<LoginStep>(stepParam as LoginStep || 'loginForm');

  // ── Step: consent ─────────────────────────────────────────────────────────
  const { email, loginAuthenticatorCode, username } = useMyVW();
  const { buildSearch } = useMyVWQueryParams();
  const [agreeConsent, setAgreeConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyErrorBanner, setVerifyErrorBanner] = useState<{ title?: string; message: string } | null>(null);

  const handleConsent = async () => {
    if (!agreeConsent) return;
    setVerifyErrorBanner(null);
    setError(null);
    setLoading(true);

    const result = await enrollInPaperless({
      username,
      loginAuthenticatorCode,
      sessionDetails,
    });

    if (!result?.data?.isSuccess) {
      setVerifyErrorBanner({
        title: i18n.t(rk.TITLE_MYVW_PAPERLESS_ENROLL_ERROR),
        message: i18n.t(rk.ERROR_MYVW_PAPERLESS_ENROLL_FAILED),
      });
      setLoading(false);
      return;
    }

    navigate('/myVW/mfa' + buildSearch({
      loginAuthenticatorCode: loginAuthenticatorCode,
      isLinked: 'false',
      username: username,
    }), { replace: true });
    setLoading(false);
    return;
  }

  // ── login Form step ──────────────────────────────────────────────────

  if (step === 'loginForm') {
    return (
      <div className={`o-page-wrap u-pb-xlarge u-mb-xlarge`}>
        {loading && <Loader isLoading />}
        <Header />
        <h1 className={styles["c-section-heading__title-header"] + ' u-mt-large'}>{i18n.t(rk.TITLE_MYVW_LOGIN)}</h1>
        <p className="u-mt">{i18n.t(rk.TEXT_MYVW_LOGIN)}</p>
        <div className="u-pb-large">
          <MyVWMarketingBanner />
        </div>
        <LoginForm onLoginSuccess={setStep} />
        <p className={styles["forgot-content"]}>
          <span>{i18n.t(rk.TEXT_FORGOT)}</span>
          {/* Initiate Retrieve username flow */}
          <Link
            value={i18n.t(rk.LABEL_RETRIEVE_USERNAME_LINK)}
            onClick={() => {
              sessionStorage.setItem("isAccountLocked", "false");
              Logger.info(
                "Retrieve Username Flow initiated",
                "/myVW/login",
                sessionDetails
              );
              navigate(i18n.t(rk.LINK_MYVW_RETRIEVE_USERNAME) + buildSearch());
            }}
            ariaLabel="Retrieve username link"
            testid="retrieve-username-link"
          />
          <span>{i18n.t(rk.TEXT_OR)}</span>
          {/* Initiate Reset password flow */}
          <Link
            value={i18n.t(rk.LABEL_RESET_PSWD_LINK)}
            onClick={() => {
              sessionStorage.setItem("isAccountLocked", "false");
              Logger.info(
                "Reset Password Flow initiated",
                "/myVW/login",
                sessionDetails
              );
              navigate(i18n.t(rk.LINK_MYVW_RESET_PASSWORD) + buildSearch());
            }}
            ariaLabel="Reset password link"
            testid="reset-password-link"
          />
        </p>

        <p className={styles["new-to-vw-content"]}>
          {i18n.t(rk.TEXT_NEW_TO_VW)}
          {/* Initiate registration flow */}
          <Link
            value={i18n.t(rk.LABEL_CREATE_ACC_LINK)}
            onClick={() => {
              sessionStorage.setItem("isAccountLocked", "false");
              Logger.info(
                "Registration Flow initiated",
                "/myVW/login",
                sessionDetails
              );
              navigate(i18n.t(rk.LINK_MYVW_CREATE_ACCOUNT) + buildSearch());
            }}
            ariaLabel="Registration link"
            testid="register-link"
          />
        </p>
      </div>
    );
  }

  // ── Consent step ──────────────────────────────────────────────────────────

  return (
    <MyVWPaperlessConsent
      email={email.toLowerCase()}
      agreeConsent={agreeConsent}
      loading={loading}
      error={error}
      verifyErrorBanner={verifyErrorBanner}
      onAgreeChange={setAgreeConsent}
      onContinue={handleConsent}
      onCancel={() => setStep('loginForm')}
      continueDisabled={!agreeConsent || loading}
      title={i18n.t(rk.TEXT_ENROLL_PAPERLESS)}
      cancelTestId="login-consent-cancel"
      continueTestId="login-consent-submit"
      checkboxTestId="login-consent-checkbox"
    />
  )
};

export default MyVWLogin;